import React from "react";
import DefaultHeadComponent from "../layouts/Head";
import { getAppStyles } from "../store/selectors/app-styles";

/**
 * Factory function to create Gatsby Head exports
 * 
 * @param {Object} options Configuration options
 * @param {React.Component} options.HeadComponent - Custom head component (defaults to main layout Head)
 * @param {Function} options.customT - Custom translation function
 * @param {Object} options.extraProps - Additional props to pass to the Head component
 * @returns {Function} A Head component function for Gatsby Head API export
 * 
 * @example
 * // Default usage (main layout Head)
 * export const Head = createHeadExport();
 * 
 * @example
 * // Custom Head component (new-website pages)
 * import { LandingHead } from "./heads";
 * export const Head = createHeadExport({ 
 *   HeadComponent: LandingHead,
 *   extraProps: { selectedIndex: 0 }
 * });
 */
export const createHeadExport = (options = {}) => {
  // Use custom HeadComponent if provided, otherwise use default
  const HeadComponent = options.HeadComponent || DefaultHeadComponent;
  
  return ({ location, pageContext }) => {
    const { interpolationTemplates } = pageContext || {};
    
    // Create a simple translation function
    const T = options.customT || ((key, params = {}) => {
      if (!key) return '';
      
      // Handle interpolation templates like {{business_name}}
      if (key.includes('{{') && key.includes('}}')) {
        let result = key;
        // Replace template keys with values from interpolationTemplates
        Object.keys(interpolationTemplates || {}).forEach((templateKey) => {
          result = result.replaceAll(`{{${templateKey}}}`, interpolationTemplates[templateKey]);
        });
        // Replace any params passed in
        Object.keys(params).forEach((paramKey) => {
          result = result.replaceAll(`{{${paramKey}}}`, params[paramKey]);
        });
        return result;
      }
      
      // Return key as-is for non-template strings
      return key;
    });

    return (
      <HeadComponent 
        {...pageContext}
        {...(options.extraProps || {})}
        location={location} 
        appStyles={getAppStyles({}, { pageContext })} 
        T={T}
        pageContext={pageContext}
      />
    );
  };
};

export default createHeadExport;
